import { useEffect, useRef, useState } from 'react';
import {
  AlertCircle, AlertTriangle, Bell, Check, Clock,
  Filter, Loader2, Phone, RefreshCw, Search,
  Shield, ShieldAlert, ShieldCheck, ShieldOff, User,
  Volume2, X, CheckCircle, XCircle, Activity,
  LayoutList, Radio, Layers,
} from 'lucide-react';
import { useArcStore } from '@/stores/arc-store';
import { useAlarmsStore } from '@/stores/alarms-store';
import { useSettingsStore, chipCls } from '@/stores/settings-store';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EventRibbon } from '@/components/EventRibbon';
import type { ArcEventWithSite } from '@/components/EventRibbon';
import { ProcessEventModal } from '@/components/ProcessEventModal';
import { ArcSubNav } from '@/components/ArcSubNav';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ArcEvent, AlarmEventSeverity, CidEventCategory, SosType } from '@asi/shared';
import * as arcService from '@/services/arc';
import { cn } from '@/lib/utils';

// ─── Constants ────────────────────────────────────────────────

const SEVERITY_CONFIG: Record<AlarmEventSeverity, { color: string; bgColor: string; icon: typeof AlertCircle }> = {
  CRITICAL: { color: '#ef4444', bgColor: '#fef2f2', icon: AlertCircle },
  ALARM: { color: '#f97316', bgColor: '#fff7ed', icon: AlertTriangle },
  WARNING: { color: '#eab308', bgColor: '#fefce8', icon: Bell },
  INFO: { color: '#3b82f6', bgColor: '#eff6ff', icon: Shield },
};

const CATEGORY_LABELS: Record<CidEventCategory, string> = {
  alarm: 'Alarma',
  restore: 'Restauración',
  open_close: 'Apertura/Cierre',
  trouble: 'Falla',
  test: 'Prueba',
  system: 'Sistema',
  bypass: 'Bypass',
};

const CATEGORY_COLORS: Record<CidEventCategory, string> = {
  alarm: 'bg-red-500/10 text-red-500',
  restore: 'bg-green-500/10 text-green-500',
  open_close: 'bg-blue-500/10 text-blue-500',
  trouble: 'bg-amber-500/10 text-amber-500',
  test: 'bg-gray-500/10 text-gray-500',
  system: 'bg-purple-500/10 text-purple-500',
  bypass: 'bg-orange-500/10 text-orange-500',
};

// CID code prefix → SOS type (standard Contact ID code ranges)
const CID_SOS_TYPE: Record<string, SosType> = {
  '100': 'medical',  // Medical alarm
  '101': 'medical',  // Personal emergency
  '102': 'medical',  // Fail to report (medical)
  '110': 'fire',
  '111': 'fire',     // Smoke
  '112': 'fire',     // Combustion
  '113': 'fire',     // Water flow
  '114': 'fire',     // Heat
  '115': 'fire',     // Pull station
  '116': 'fire',     // Duct
  '117': 'fire',     // Flame
  '118': 'fire',     // Near alarm (fire)
  '120': 'panic',    // Panic
  '121': 'panic',    // Duress
  '122': 'panic',    // Silent
  '123': 'panic',    // Audible
  '124': 'panic',    // Duress access
  '125': 'panic',    // Duress code
  '126': 'panic',    // Holdup suspicion
  '129': 'panic',    // Panic verifier
  '130': 'intrusion', // Burglary
  '131': 'intrusion', // Perimeter
  '132': 'intrusion', // Interior
  '133': 'intrusion', // 24 hour
  '134': 'intrusion', // Entry/exit
  '135': 'intrusion', // Day/night
  '136': 'intrusion', // Outdoor
  '137': 'intrusion', // Tamper
  '138': 'intrusion', // Near alarm
  '139': 'intrusion', // Intrusion verifier
  '161': 'vehicle',  // Missing supervisor
  '162': 'vehicle',  // High-tag
};

const SOS_TYPE_CONFIG: Record<SosType, { label: string; emoji: string; color: string }> = {
  sos:       { label: 'SOS',       emoji: '🆘', color: 'bg-red-500/15 text-red-600 dark:text-red-400' },
  medical:   { label: 'Médico',    emoji: '🏥', color: 'bg-blue-500/15 text-blue-600 dark:text-blue-400' },
  fire:      { label: 'Incendio',  emoji: '🔥', color: 'bg-orange-500/15 text-orange-600 dark:text-orange-400' },
  intrusion: { label: 'Intrusión', emoji: '🚨', color: 'bg-red-600/15 text-red-700 dark:text-red-400' },
  vehicle:   { label: 'Vehículo',  emoji: '🚗', color: 'bg-gray-500/15 text-gray-600 dark:text-gray-400' },
  panic:     { label: 'Pánico',    emoji: '😨', color: 'bg-purple-500/15 text-purple-600 dark:text-purple-400' },
  other:     { label: 'Otro',      emoji: '⚠️', color: 'bg-yellow-500/15 text-yellow-600 dark:text-yellow-400' },
};

function getSosTypeFromCid(cidCode: string): SosType | null {
  // Strip qualifier prefix (E/R) and leading zeros — use first 3 digits of numeric part
  const numeric = cidCode.replace(/^[ER]/i, '').replace(/^0+/, '');
  return CID_SOS_TYPE[numeric.padStart(3, '0')] ?? null;
}

// ─── Utility Functions ────────────────────────────────────────

const GT_TIMEZONE = 'America/Guatemala';

function formatTime(date: string): string {
  return new Date(date).toLocaleTimeString('es-GT', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: GT_TIMEZONE,
  });
}

function formatDateTime(date: string): string {
  return new Date(date).toLocaleString('es-GT', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: GT_TIMEZONE,
  });
}

function formatResponseTime(seconds: number | null): string {
  if (seconds === null) return '-';
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

// ─── Resolve Modal ──────────────────────────────────────────

function ResolveModal({
  eventId,
  cidCode,
  severity,
  receivedAt,
  onClose,
  onConfirm,
}: {
  eventId: string;
  cidCode: string;
  severity: AlarmEventSeverity;
  receivedAt?: string;
  onClose: () => void;
  onConfirm: (eventId: string, resolution: string) => Promise<void>;
}) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const config = SEVERITY_CONFIG[severity];
  const Icon = config.icon;

  async function handleConfirm() {
    if (!text.trim()) return;
    setSaving(true);
    await onConfirm(eventId, text.trim());
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-background border rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl" style={{ backgroundColor: config.bgColor }}>
              <Icon className="h-5 w-5" style={{ color: config.color }} />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Confirmar cierre de evento</h3>
              <p className="text-sm text-muted-foreground font-mono">{cidCode}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {receivedAt && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/40 rounded-lg px-3 py-2">
              <Clock className="h-4 w-4 flex-shrink-0" />
              <span>Recibido: {formatDateTime(receivedAt)}</span>
            </div>
          )}
          <div>
            <label className="text-sm font-medium block mb-1.5">
              Descripción de la resolución <span className="text-destructive">*</span>
            </label>
            <textarea
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              rows={3}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleConfirm(); }}
              placeholder="Ej: Verificado con cliente, falsa alarma. Se confirmó código de cancelación. Sin despacho requerido."
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
            />
            <p className="text-xs text-muted-foreground mt-1">Ctrl + Enter para confirmar</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-5 pt-0">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-input rounded-xl text-sm font-medium hover:bg-muted transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={!text.trim() || saving}
            className="flex-1 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            Confirmar cierre
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Event Card Component ─────────────────────────────────────

function EventCard({
  event,
  onAcknowledge,
  onResolve,
  onSelect,
}: {
  event: ArcEvent;
  onAcknowledge: () => void;
  onResolve: () => void;
  onSelect: () => void;
}) {
  const config = SEVERITY_CONFIG[event.severity];
  const Icon = config.icon;
  const isUnacked = !event.acknowledged_at;
  const isUnresolved = !event.resolved_at;

  return (
    <div
      className={cn(
        'p-4 border rounded-lg transition-all cursor-pointer',
        isUnacked && event.severity === 'CRITICAL' && 'animate-pulse border-red-500 bg-red-50 dark:bg-red-950/20',
        isUnacked && event.severity === 'ALARM' && 'border-orange-400 bg-orange-50 dark:bg-orange-950/20',
        !isUnacked && 'bg-card hover:bg-muted/50'
      )}
      onClick={onSelect}
    >
      <div className="flex items-start gap-3">
        {/* Severity Icon */}
        <div
          className="p-2 rounded-lg flex-shrink-0"
          style={{ backgroundColor: config.bgColor }}
        >
          <Icon className="h-5 w-5" style={{ color: config.color }} />
        </div>

        {/* Event Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-sm font-medium">
              {event.cid_qualifier === 'E' ? '🔴' : '🟢'} {event.cid_code}
            </span>
            <Badge
              variant="secondary"
              className={cn('text-xs', CATEGORY_COLORS[event.event_category])}
            >
              {CATEGORY_LABELS[event.event_category]}
            </Badge>
            {(() => {
              const sosType = getSosTypeFromCid(event.cid_code);
              if (!sosType) return null;
              const cfg = SOS_TYPE_CONFIG[sosType];
              return (
                <Badge variant="secondary" className={cn('text-xs', cfg.color)}>
                  {cfg.emoji} {cfg.label}
                </Badge>
              );
            })()}
            {event.zone_name && (
              <span className="text-xs text-muted-foreground truncate">
                {event.zone_name}
              </span>
            )}
          </div>

          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {formatTime(event.received_at)}
            </span>
            {(event.user_name || event.user_code) && (
              <span className="flex items-center gap-1">
                <User className="h-3.5 w-3.5" />
                {event.user_name ?? `Código ${event.user_code}`}
              </span>
            )}
            {event.partition_no > 1 && (
              <span className="text-xs">P{event.partition_no}</span>
            )}
          </div>

          {/* Status Indicators */}
          <div className="flex items-center gap-2 mt-2">
            {event.acknowledged_at ? (
              <span className="text-xs text-green-600 flex items-center gap-1">
                <CheckCircle className="h-3 w-3" /> Reconocido
              </span>
            ) : (
              <span className="text-xs text-amber-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" /> Pendiente
              </span>
            )}
            {event.resolved_at ? (
              <span className="text-xs text-green-600 flex items-center gap-1">
                <CheckCircle className="h-3 w-3" /> Resuelto
              </span>
            ) : event.acknowledged_at ? (
              <span className="text-xs text-blue-600 flex items-center gap-1">
                <Activity className="h-3 w-3" /> En proceso
              </span>
            ) : null}
            {event.response_time_seconds !== null && (
              <span className="text-xs text-muted-foreground">
                ⏱️ {formatResponseTime(event.response_time_seconds)}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex-shrink-0 flex gap-1" onClick={(e) => e.stopPropagation()}>
          {isUnacked && (
            <Button variant="outline" size="sm" onClick={onAcknowledge}>
              <Check className="h-4 w-4 mr-1" />
              Procesar
            </Button>
          )}
          {!isUnacked && isUnresolved && (
            <Button variant="outline" size="sm" onClick={onResolve}>
              <CheckCircle className="h-4 w-4 mr-1" />
              Resolver
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Stats Cards ──────────────────────────────────────────────

function StatsCards({
  stats,
  pendingCount,
}: {
  stats: {
    total: number;
    byCategory: Record<string, number>;
    bySeverity: Record<string, number>;
    responseTimeAvg: number | null;
  } | null;
  pendingCount: number;
}) {
  if (!stats) return null;

  const resolved = Math.max(0, stats.total - pendingCount);
  const pctAtendidos = stats.total > 0 ? Math.round((resolved / stats.total) * 100) : 100;
  const critical = stats.bySeverity['CRITICAL'] ?? 0;
  const alarm = stats.bySeverity['ALARM'] ?? 0;

  return (
    <div className="grid grid-cols-4 gap-4 mb-6">
      <Card>
        <CardContent className="pt-4">
          <div className="text-2xl font-bold">{stats.total.toLocaleString('es-GT')}</div>
          <div className="text-sm text-muted-foreground">Total eventos 24h</div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-4">
          <div className="text-2xl font-bold text-red-500">{(critical + alarm).toLocaleString('es-GT')}</div>
          <div className="text-sm text-muted-foreground">Alarmas</div>
          <div className="text-xs text-muted-foreground mt-0.5">{critical} críticos / {alarm} alarma</div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-4">
          <div className={`text-2xl font-bold ${pctAtendidos >= 90 ? 'text-green-500' : pctAtendidos >= 60 ? 'text-amber-500' : 'text-red-500'}`}>
            {pctAtendidos}%
          </div>
          <div className="text-sm text-muted-foreground">% Atendidos 24h</div>
          <div className="text-xs text-muted-foreground mt-0.5">{resolved.toLocaleString('es-GT')} / {stats.total.toLocaleString('es-GT')}</div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-4">
          <div className="text-2xl font-bold">
            {stats.responseTimeAvg !== null
              ? formatResponseTime(Math.round(stats.responseTimeAvg))
              : '-'
            }
          </div>
          <div className="text-sm text-muted-foreground">Tiempo promedio respuesta</div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Batch Process Modal ──────────────────────────────────────
// Pure form — collects inputs then fires immediately.
// Progress tracked in BatchProgressToast; operator can monitor while batch runs.

function BatchProcessModal({
  events,
  siteLabel,
  siteId,
  onClose,
  onConfirm,
}: {
  events: ArcEvent[];
  siteLabel?: string;
  siteId?: string;
  onClose: () => void;
  onConfirm: (resolution: string, categoryFilter: string) => void;
}) {
  const pendingEvents = events.filter((e) => !e.acknowledged_at);
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');
  const [resolution, setResolution] = useState('');
  const [passwordPhrase, setPasswordPhrase] = useState<string | null>(null);
  const quickReplies = useSettingsStore((s) =>
    s.quickReplies.filter((r) => r.modals === 'batch' || r.modals === 'both' || r.modals === 'all').sort((a, b) => a.order - b.order)
  );

  // Fetch the site's verbal password so the operator can confirm identity
  // with the client before processing the batch (mirrors ProcessEventModal).
  useEffect(() => {
    if (!siteId) { setPasswordPhrase(null); return; }
    let cancelled = false;
    arcService.fetchSiteDetail(siteId).then((siteData) => {
      if (!cancelled) setPasswordPhrase(siteData?.clave ?? null);
    }).catch(() => { if (!cancelled) setPasswordPhrase(null); });
    return () => { cancelled = true; };
  }, [siteId]);

  const filteredPending = categoryFilter === 'ALL'
    ? pendingEvents
    : pendingEvents.filter((e) => e.event_category === categoryFilter);
  const displayCount = filteredPending.length;
  const categoryOptions = Array.from(new Set(pendingEvents.map((e) => e.event_category)));

  function handleConfirm() {
    if (!resolution.trim()) return;
    onConfirm(resolution.trim(), categoryFilter);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-background border rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-amber-500/10">
              <Layers className="h-5 w-5 text-amber-500" />
            </div>
            <div>
              <h3 className="font-semibold">Procesamiento en lote</h3>
              {siteLabel ? (
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{siteLabel}</span>
                  {' · '}{pendingEvents.length} eventos pendientes
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">{pendingEvents.length} eventos pendientes</p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {passwordPhrase && (
            <div className="flex items-center gap-1.5 text-xs rounded-lg bg-amber-500/10 border border-amber-500/20 px-3 py-2">
              <span className="font-semibold text-amber-700 dark:text-amber-400">Palabra Clave del sitio:</span>
              <span className="font-mono font-bold text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 rounded">
                {passwordPhrase}
              </span>
            </div>
          )}
          <div>
            <label className="text-sm font-medium block mb-1.5">Categoría a procesar</label>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todas las categorías ({pendingEvents.length} eventos)</SelectItem>
                {categoryOptions.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {CATEGORY_LABELS[cat as CidEventCategory] ?? cat} (
                    {pendingEvents.filter((e) => e.event_category === cat).length} eventos)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
            Se procesarán <strong>todos los eventos pendientes</strong> que coincidan con el filtro seleccionado.
            <span className="block text-xs mt-0.5 font-semibold">Esta acción no se puede deshacer.</span>
          </div>

          <div>
            <label className="text-sm font-medium block mb-1.5">
              Razón del procesamiento en lote <span className="text-destructive">*</span>
            </label>
            {quickReplies.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {quickReplies.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setResolution(r.text)}
                    className={`px-2.5 py-0.5 text-xs rounded-full border transition-colors ${chipCls(r.color)}`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            )}
            <textarea
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              rows={3}
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && resolution.trim()) handleConfirm(); }}
              placeholder="Ej: Pruebas periódicas del sistema verificadas. Sin incidentes reales."
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
            />
            <p className="text-xs text-muted-foreground mt-1">Obligatorio · Ctrl + Enter para procesar</p>
          </div>
        </div>

        <div className="flex gap-3 p-5 pt-0">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-input rounded-xl text-sm font-medium hover:bg-muted transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={!resolution.trim()}
            className="flex-1 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Layers className="h-4 w-4" />
            {displayCount > 0 ? `Procesar ${displayCount}+ eventos` : 'Procesar eventos'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Batch Progress Toast ─────────────────────────────────────
// Non-blocking floating card — shows batch progress in the corner
// while the operator keeps monitoring the ribbon.

interface BatchJob {
  label: string;
  progress: number;
  status: 'processing' | 'done' | 'error';
  processed?: number;
  error?: string;
}

function BatchProgressToast({ job, onDismiss }: { job: BatchJob; onDismiss: () => void }) {
  const isDone  = job.status === 'done';
  const isError = job.status === 'error';

  return (
    <div className="fixed bottom-5 right-5 z-50 w-80 rounded-2xl border shadow-2xl bg-background p-4 flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <div className={`p-1.5 rounded-lg flex-shrink-0 ${isError ? 'bg-destructive/10' : isDone ? 'bg-green-500/10' : 'bg-amber-500/10'}`}>
            {isError
              ? <XCircle className="h-4 w-4 text-destructive" />
              : isDone
                ? <CheckCircle className="h-4 w-4 text-green-500" />
                : <Loader2 className="h-4 w-4 text-amber-500 animate-spin" />
            }
          </div>
          <div>
            <p className="text-sm font-semibold leading-none">
              {isError ? 'Error en lote' : isDone ? 'Lote procesado' : 'Procesando lote...'}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[180px]">{job.label}</p>
          </div>
        </div>
        <button onClick={onDismiss} className="p-1 rounded-md hover:bg-muted transition-colors flex-shrink-0">
          <X className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      </div>

      {!isError && (
        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs text-muted-foreground">
              {isDone
                ? `${job.processed?.toLocaleString('es-GT') ?? 0} eventos procesados`
                : 'Enviando al servidor...'}
            </span>
            <span className="text-xs font-bold tabular-nums text-amber-600">{job.progress}%</span>
          </div>
          <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${job.progress}%`, background: isDone ? '#22c55e' : '#d97706' }}
            />
          </div>
        </div>
      )}

      {isError && <p className="text-xs text-destructive">{job.error}</p>}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export function ArcEventsPage() {
  const arcStore = useArcStore();
  const alarmsStore = useAlarmsStore();
  const [search, setSearch] = useState('');
  const [severityFilter, setSeverityFilter] = useState<string>('ALL');
  const [acknowledgedFilter, setAcknowledgedFilter] = useState<string>('PENDING');
  const [viewMode, setViewMode] = useState<'list' | 'ribbon'>('ribbon');
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [batchSiteConfig, setBatchSiteConfig] = useState<{ label: string; siteId?: string; events: ArcEventWithSite[] } | null>(null);
  const [ribbonRefreshKey, setRibbonRefreshKey] = useState(0);
  // Events currently visible in the ribbon (after tab + site/severity/search filters).
  // Drives the batch counter/button when viewMode === 'ribbon' so the operator can
  // process exactly what they're seeing.
  const [ribbonFilteredEvents, setRibbonFilteredEvents] = useState<ArcEventWithSite[]>([]);
  // Ref always holds the LATEST filtered events — avoids stale closures in the
  // batch button onClick and onConfirm when React state hasn't flushed yet.
  const ribbonFilteredEventsRef = useRef<ArcEventWithSite[]>([]);
  const [batchJob, setBatchJob] = useState<BatchJob | null>(null);
  const batchProgressRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [resolveTarget, setResolveTarget] = useState<{
    id: string;
    cidCode: string;
    severity: AlarmEventSeverity;
    receivedAt?: string;
  } | null>(null);
  // Modal de procesamiento — abierto desde la cinta o la lista
  const [processModalEvent, setProcessModalEvent] = useState<ArcEventWithSite | null>(null);

  useEffect(() => {
    // Serialize initial queries to avoid concurrent auth-lock contention.
    // Each Supabase request needs the auth token lock; firing 5+ in parallel
    // can exhaust the lock queue and cause a 5-second deadlock cascade.
    let cancelled = false;
    (async () => {
      await arcStore.fetchEvents();
      if (cancelled) return;
      arcStore.reconcilePendingEvents();
      arcStore.fetchCidCodes();
    })();
    return () => { cancelled = true; };

    // NOTE: subscribeToEvents() (Supabase Realtime → store) is intentionally NOT
    // called here. EventRibbon already has its own 'arc-events-ribbon' Supabase
    // channel for direct INSERT delivery. Having two simultaneous subscriptions to
    // the same table INSERT caused duplicate events in React 18's batched updates.
    // The store receives new events via the gateway WebSocket (ArcRealtimeProvider).

    // Visibility-based reconciliation is handled centrally by ArcRealtimeProvider.
  }, []);

  // Called by both ribbon + list "Procesar" buttons — opens processing modal
  const handleEventDetail = (ribbonEvent: ArcEventWithSite) => {
    setProcessModalEvent(ribbonEvent);
  };

  const handleProcess = async (eventId: string, notes?: string) => {
    await arcStore.processEvent(eventId, notes);
  };

  const handleResolve = (eventId: string, cidCode?: string, severity?: AlarmEventSeverity, receivedAt?: string) => {
    setResolveTarget({ id: eventId, cidCode: cidCode ?? '', severity: severity ?? 'INFO', receivedAt });
  };

  const handleConfirmResolve = async (eventId: string, resolution: string) => {
    await arcStore.resolveEvent(eventId, resolution);
    setResolveTarget(null);
  };



  // Filter events (dedup by ID as safety net for concurrent Realtime + WS updates)
  const filteredEvents = arcStore.events
    .filter((event, index, self) => index === self.findIndex((e) => e.id === event.id))
    .filter((event) => {
      if (severityFilter !== 'ALL' && event.severity !== severityFilter) return false;
      if (acknowledgedFilter === 'PENDING' && event.acknowledged_at) return false;
      if (acknowledgedFilter === 'ACKNOWLEDGED' && !event.acknowledged_at) return false;
      if (search) {
        const searchLower = search.toLowerCase();
        return (
          event.cid_code.includes(search) ||
          event.zone_name?.toLowerCase().includes(searchLower) ||
          event.user_name?.toLowerCase().includes(searchLower) ||
          event.user_code?.toLowerCase().includes(searchLower)
        );
      }
      return true;
    })
    .sort((a, b) => {
      // Unresolved events first
      const aRes = a.resolved_at ? 1 : 0;
      const bRes = b.resolved_at ? 1 : 0;
      if (aRes !== bRes) return aRes - bRes;
      // Then by priority (Robo > Pánico > Médico > Incendio > … > Cierre)
      const ap = arcService.getEventPriority(a);
      const bp = arcService.getEventPriority(b);
      if (ap !== bp) return ap - bp;
      // Newest first as tiebreaker
      return new Date(b.received_at).getTime() - new Date(a.received_at).getTime();
    });

  // Real DB count (not capped by in-memory array). Falls back to in-memory
  // count while the first reconcile is still loading so the badge never reads 0.
  const inMemoryPending = arcStore.events.filter((e) => !e.acknowledged_at).length;
  const pendingCount = arcStore.pendingCount ?? inMemoryPending;
  const criticalCount = arcStore.events.filter(
    (e) => !e.acknowledged_at && (e.severity === 'CRITICAL' || e.severity === 'ALARM')
  ).length;

  // Source of truth for the "X eventos" badge and "Procesar lote" button:
  // when in cinta mode, use what the ribbon is actually showing (respects its
  // tab + site/category/search filters); in lista mode, use the lista's filteredEvents.
  const visibleEvents: ArcEventWithSite[] = viewMode === 'ribbon'
    ? ribbonFilteredEvents
    : (filteredEvents as unknown as ArcEventWithSite[]);
  const visiblePending = visibleEvents.filter((e) => !e.acknowledged_at);

  return (
    <div className="h-full flex flex-col p-6">
      {/* ARC module sub-navigation */}
      <ArcSubNav />
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldAlert className="h-7 w-7" />
            Centro de Eventos ARC
          </h1>
          <p className="text-muted-foreground">
            {pendingCount > 0 ? (
              <span className="text-amber-600">
                {pendingCount} eventos pendientes
                {criticalCount > 0 && ` (${criticalCount} críticos)`}
              </span>
            ) : (
              'Todos los eventos atendidos'
            )}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Gateway Connection Status */}
          <div className="flex items-center gap-2 border rounded-lg px-3 py-1.5 bg-muted/50">
            <span className="text-xs text-muted-foreground">HPP:</span>
            {arcStore.wsConnected ? (
              <span className="flex items-center gap-1 text-xs text-green-600">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                Conectado
              </span>
            ) : arcStore.wsState === 'connecting' ? (
              <span className="flex items-center gap-1 text-xs text-amber-500">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                </span>
                Conectando
              </span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <span className="relative inline-flex rounded-full h-2 w-2 bg-gray-400"></span>
                Desconectado
              </span>
            )}
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center border rounded-lg p-1">
            <Button
              variant={viewMode === 'ribbon' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('ribbon')}
              className="h-8 px-3"
            >
              <Radio className="h-4 w-4 mr-1" />
              Cinta
            </Button>
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="h-8 px-3"
            >
              <LayoutList className="h-4 w-4 mr-1" />
              Lista
            </Button>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => arcStore.fetchEvents()}
            disabled={arcStore.loading}
          >
            {arcStore.loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por código CID, zona, usuario..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={severityFilter} onValueChange={setSeverityFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Severidad" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todas</SelectItem>
            <SelectItem value="CRITICAL">Crítico</SelectItem>
            <SelectItem value="ALARM">Alarma</SelectItem>
            <SelectItem value="WARNING">Advertencia</SelectItem>
            <SelectItem value="INFO">Info</SelectItem>
          </SelectContent>
        </Select>

        <Select value={acknowledgedFilter} onValueChange={setAcknowledgedFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todos</SelectItem>
            <SelectItem value="PENDING">Pendientes</SelectItem>
            <SelectItem value="ACKNOWLEDGED">Reconocidos</SelectItem>
          </SelectContent>
        </Select>

        <Badge variant="secondary">
          {visibleEvents.length} eventos
        </Badge>

        {/* Batch process button — reflects what's visible when there is something to
            process; otherwise falls back to the global pending count so the operator
            can still launch a global batch even when the visible window is empty. */}
        {(() => {
          const allPending = arcStore.events.filter((e) => !e.acknowledged_at) as unknown as ArcEventWithSite[];
          const batchSet = visiblePending.length > 0 ? visiblePending : allPending;
          const enabled = batchSet.length > 0;
          return (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                // Always snapshot at click time using the ref (avoids stale-closure /
                // timing issues when the ribbon's siteFilter update hasn't flushed yet).
                const currentVisible = viewMode === 'ribbon'
                  ? ribbonFilteredEventsRef.current
                  : (filteredEvents as unknown as ArcEventWithSite[]);
                const currentPending = currentVisible.filter((e) => !e.acknowledged_at);
                const effectiveEvents = currentPending.length > 0 ? currentPending : allPending;
                const label = currentPending.length > 0 && currentPending.length < allPending.length
                  ? `Cuenta seleccionada (${currentPending.length} eventos)`
                  : 'Todos los eventos pendientes';
                setBatchSiteConfig({ label, siteId: undefined, events: effectiveEvents });
                setBatchModalOpen(true);
              }}
              disabled={!enabled}
              title={visiblePending.length === 0 && allPending.length > 0
                ? `Procesar todos los ${allPending.length} eventos pendientes`
                : undefined}
            >
              <Layers className="h-4 w-4 mr-1.5" />
              Procesar lote ({batchSet.length})
            </Button>
          );
        })()}
      </div>

      {/* Events - Ribbon or List View */}
      {viewMode === 'ribbon' ? (
        <Card className="flex-1 flex flex-col overflow-hidden">
          <CardHeader className="pb-3 flex-shrink-0">
            <CardTitle className="text-lg flex items-center gap-2">
              <Radio className="h-5 w-5 text-green-500" />
              Cinta Histórica en Tiempo Real
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 min-h-0 overflow-hidden p-0 pb-0">
            <EventRibbon
              maxEvents={5000}
              autoScroll
              onProcess={setProcessModalEvent}
              onBatchBySite={(siteId, siteName, events) => {
                setBatchSiteConfig({ label: siteName, siteId, events });
                setBatchModalOpen(true);
              }}
              onFilteredChange={(events) => {
                  ribbonFilteredEventsRef.current = events;
                  setRibbonFilteredEvents(events);
                }}
              severityFilter={severityFilter}
              acknowledgedFilter={acknowledgedFilter}
              search={search}
              refreshTrigger={ribbonRefreshKey}
            />
          </CardContent>
        </Card>
      ) : (
        /* Events List */
        <div className="flex-1 overflow-y-auto">
        {arcStore.loading && arcStore.events.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <ShieldCheck className="h-12 w-12 mb-4 opacity-50" />
            <p>No hay eventos que mostrar</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredEvents.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                onAcknowledge={() => setProcessModalEvent(event as unknown as ArcEventWithSite)}
                onResolve={() => handleResolve(event.id, event.cid_code, event.severity, event.received_at)}
                onSelect={() => arcStore.selectEvent(event)}
              />
            ))}
          </div>
        )}
      </div>
      )}
      {resolveTarget && (
        <ResolveModal
          eventId={resolveTarget.id}
          cidCode={resolveTarget.cidCode}
          severity={resolveTarget.severity}
          receivedAt={resolveTarget.receivedAt}
          onClose={() => setResolveTarget(null)}
          onConfirm={handleConfirmResolve}
        />
      )}
      {/* Modal de procesamiento — un solo paso: reconoce + cierra el evento */}
      <ProcessEventModal
        event={processModalEvent}
        onClose={() => setProcessModalEvent(null)}
        onProcess={handleProcess}
      />
      {/* Batch Process Modal */}
      {batchModalOpen && (
        <BatchProcessModal
          events={(batchSiteConfig?.events ?? visiblePending) as ArcEvent[]}
          siteLabel={batchSiteConfig?.label}
          siteId={batchSiteConfig?.siteId}
          onClose={() => { setBatchModalOpen(false); setBatchSiteConfig(null); }}
          onConfirm={(resolution, categoryFilter) => {
            const label = batchSiteConfig?.label ?? 'Todos los eventos';

            // Compute the exact IDs the operator sees in the modal (same logic as BatchProcessModal's
            // filteredPending) so we never process more events than what is visible.
            // This is critical when a search/client filter is active — the server-side filter only
            // knows siteId+category, not the client-side search text.
            const sourceEvents = batchSiteConfig?.events ?? (visiblePending as ArcEvent[]);
            const pendingToProcess = sourceEvents.filter(
              (e) => !e.acknowledged_at && (categoryFilter === 'ALL' || e.event_category === categoryFilter)
            );
            // Always send the exact IDs the operator sees so processing is scoped to
            // what is visible, regardless of active filters. The Edge Function handles
            // large arrays by chunking the `.in()` calls (avoids PostgREST URL limits).
            const eventIds = pendingToProcess.map((e) => e.id);

            // Close modal immediately — process runs in background
            setBatchModalOpen(false);
            setBatchSiteConfig(null);
            // Start floating progress toast
            let current = 0;
            if (batchProgressRef.current) clearInterval(batchProgressRef.current);
            setBatchJob({ label, progress: 0, status: 'processing' });
            batchProgressRef.current = setInterval(() => {
              current += (92 - current) * 0.07;
              setBatchJob((j) => j ? { ...j, progress: Math.min(Math.round(current), 92) } : j);
            }, 250);
            // Fire and forget — ribbon stays fully interactive
            arcStore.processBatchByFilter({
              siteId: batchSiteConfig?.siteId,
              category: categoryFilter === 'ALL' ? undefined : categoryFilter,
              resolution,
              eventIds,
            }).then((processed) => {
              clearInterval(batchProgressRef.current!);
              setBatchJob({ label, progress: 100, status: 'done', processed: undefined });
              setRibbonRefreshKey((k) => k + 1);
              setTimeout(() => setBatchJob(null), 4000);
            }).catch((err: Error) => {
              clearInterval(batchProgressRef.current!);
              setBatchJob({ label, progress: 0, status: 'error', error: err.message });
            });
          }}
        />
      )}
      {/* Background batch progress toast */}
      {batchJob && (
        <BatchProgressToast job={batchJob} onDismiss={() => setBatchJob(null)} />
      )}
    </div>
  );
}

export default ArcEventsPage;
